import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface OptimisticUpdateOptions {
  table: "decision_steps" | "decision_sessions" | "decision_matrix_entries" | "decision_scenarios";
  id: string;
  rowVersion: number;
  updates: Record<string, unknown>;
}

/**
 * Performs an optimistic-locking update: only succeeds if row_version matches.
 * Returns { success, newRowVersion } or shows a conflict toast.
 */
export async function optimisticUpdate({
  table,
  id,
  rowVersion,
  updates,
}: OptimisticUpdateOptions): Promise<{ success: boolean; newRowVersion: number | null }> {
  const { data, error } = await supabase
    .from(table)
    .update(updates)
    .eq("id", id)
    .eq("row_version", rowVersion)
    .select("row_version")
    .maybeSingle();

  if (error) {
    console.error(`Optimistic update failed on ${table}:`, error);
    toast({
      title: "Speichern fehlgeschlagen",
      description: error.message,
      variant: "destructive",
    });
    return { success: false, newRowVersion: null };
  }

  if (!data) {
    // 0 rows matched → version conflict
    toast({
      title: "Daten wurden geändert",
      description:
        "Jemand hat diese Daten zwischenzeitlich bearbeitet. Bitte lade die Seite neu.",
      variant: "destructive",
    });
    return { success: false, newRowVersion: null };
  }

  return { success: true, newRowVersion: data.row_version };
}
